import { getLegalShieldLink } from '../../../config/legalshield-links';
import { trackAffiliateClick } from '../../../utils/analytics';

export default function BusinessPath() {
  const handleLandlordClick = () => {
    trackAffiliateClick('Landlord Supplement', getLegalShieldLink('LANDLORD_SUPPLEMENT_URL'), 'Home');
  };

  const handleBusinessClick = () => {
    trackAffiliateClick('Business Plan', getLegalShieldLink('BUSINESS_PLAN_URL'), 'Home');
  };

  return (
    <section id="path-business" className="relative py-12 sm:py-16 md:py-20 bg-[#0a1628]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-4 sm:mb-6 leading-tight" style={{ fontFamily: "'Roboto', sans-serif" }}>
            PATH 2: I OWN <span className="text-[#DC2626]">RENTALS OR A BUSINESS</span>
          </h2>
        </div>

        {/* Big Red Warning Box */}
        <div className="bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-2xl p-6 sm:p-8 md:p-10 mb-8 sm:mb-12 border-4 border-[#DC2626] shadow-2xl">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-fill text-3xl sm:text-4xl text-white"></i>
            </div>
            <div>
              <h3 className="text-white font-black text-xl sm:text-2xl md:text-3xl mb-3 sm:mb-4 uppercase">
                WARNING: DO NOT BUY THE WRONG SHIELD
              </h3>
              <div className="space-y-3 sm:space-y-4 text-white text-base sm:text-lg leading-relaxed">
                <p>
                  <span className="font-black">The Personal Plan only covers YOU as an individual.</span> It does NOT 
                  cover your LLC, your rental properties, or your business disputes.
                </p>
                <p>
                  If you're a landlord dealing with a tenant, or a business owner with a vendor problem, 
                  the Personal Plan won't help you. You need business coverage.
                </p>
                <p className="font-black text-lg sm:text-xl">
                  Don't be cheap. Get the right armor.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SEO H3 - Landlord/Business Keyword */}
        <div className="mt-8 mb-6">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center leading-tight">
            Legal protection for landlords and small business owners.
          </h3>
        </div>

        {/* Two Options Layout */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {/* Option 1: Side-Hustle Landlord */}
          <div className="bg-gradient-to-br from-[#0f1d2e] to-[#1a2332] rounded-2xl p-6 sm:p-8 border-2 border-[#DC2626]/30">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#DC2626]/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-home-4-fill text-2xl sm:text-3xl text-[#DC2626]"></i>
            </div>
            
            <h4 className="text-xl sm:text-2xl font-black text-white mb-3 sm:mb-4">
              Small Landlord / Side Hustle
            </h4>
            
            <p className="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
              If you have a few rentals or a side hustle
            </p>

            {/* Micro-copy above button */}
            <div className="mb-4 sm:mb-6 bg-blue-950/30 border border-blue-500/30 rounded-lg p-3 sm:p-4">
              <p className="text-blue-300 text-sm sm:text-base leading-relaxed">
                <strong className="text-blue-200">On the next page:</strong> Choose the Personal plan and add the <strong className="text-white">Home Business Supplement</strong> at checkout.
              </p>
            </div>

            {/* CTA Button */}
            <a
              href={getLegalShieldLink('LANDLORD_SUPPLEMENT_URL')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLandlordClick}
              className="w-full inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-xs sm:text-sm md:text-base lg:text-lg px-4 sm:px-6 md:px-8 py-4 sm:py-5 rounded-lg transition-all duration-300 shadow-xl hover:scale-105 cursor-pointer leading-tight text-center"
            >
              <span>ACTIVATE LANDLORD SHIELD (SIDE HUSTLE)</span>
              <i className="ri-arrow-right-line text-lg sm:text-xl md:text-2xl flex-shrink-0"></i>
            </a>
          </div>

          {/* Option 2: Serious Business */}
          <div className="bg-gradient-to-br from-[#0f1d2e] to-[#1a2332] rounded-2xl p-6 sm:p-8 border-2 border-[#DC2626]/30">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#DC2626]/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-building-2-fill text-2xl sm:text-3xl text-[#DC2626]"></i>
            </div>
            
            <h4 className="text-xl sm:text-2xl font-black text-white mb-3 sm:mb-4">
              Serious Business / Multiple Properties
            </h4>
            
            <p className="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
              If your business is its own beast
            </p>

            {/* CTA Button */}
            <a
              href={getLegalShieldLink('BUSINESS_PLAN_URL')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBusinessClick}
              className="w-full inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-sm sm:text-base md:text-lg px-6 sm:px-8 py-4 sm:py-5 rounded-lg transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              ACTIVATE BUSINESS SHIELD
              <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
            </a>
          </div>
        </div>

        {/* Micro-copy */}
        <div className="bg-[#0f1d2e] border border-[#DC2626]/20 rounded-xl p-4 sm:p-6 mt-6 sm:mt-8">
          <div className="flex items-start gap-2 sm:gap-3">
            <i className="ri-information-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-1"></i>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              <span className="text-white font-bold">Again, ignore anything about "joining the opportunity."</span> You're 
              just buying coverage for your business. No MLM, no recruiting, no nonsense.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}